import { useState, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Box,
  Divider,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { generarTicketVenta } from "./generarTicketVenta";
import Autocomplete from "@mui/material/Autocomplete";
import { useSubscriptionStatus } from "../useSubscriptionStatus";
import { cloudSync } from '../../utils/cloudStorage';
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Alert from '@mui/material/Alert';

export default function POSVenta({ state }) {
  const [carrito, setCarrito] = useState([]);
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [openNuevo, setOpenNuevo] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [categoriaFiltro, setCategoriaFiltro] = useState("TODAS");
  const [nuevoProd, setNuevoProd] = useState({
    nombre: "",
    categoria: "",
    precio: ""
  });
  const { status } = useSubscriptionStatus('fastfood_pos_v1');

  const [syncing, setSyncing] = useState(false);
  const [done, setDone] = useState(false);
  const [syncError, setSyncError] = useState(null);

  const handleSync = async () => {
    setSyncing(true);
    setDone(false);
    
    setSyncError(null);
    try {
      await cloudSync.pushAll();
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    } catch (err) {
      setSyncError(err.response?.data?.message || 'No se pudo guardar. Tus cambios locales se conservan.');
    } finally { setSyncing(false); }
  };

  const categorias = useMemo(() => {
    return [
      "TODAS",
      ...new Set(
        state.productos
          .filter(p => p.activo)
          .map(p => p.categoria)
          .filter(Boolean)
      )
    ];
  }, [state.productos]);

  const productosFiltrados = useMemo(() => {
    return state.productos
      .filter(p => p.activo)
      .filter(p =>
        p.nombre.toLowerCase().includes(busqueda.toLowerCase())
      )
      .filter(p =>
        categoriaFiltro === "TODAS"
          ? true
          : p.categoria === categoriaFiltro
      );
  }, [state.productos, busqueda, categoriaFiltro]);


  /* =========================
     LÓGICA DE CARRITO
  ========================== */

  const agregarProducto = async (producto) => {
    setCarrito(prev => {
      const existe = prev.find(i => i.id === producto.id);
      if (existe) {
        return prev.map(i =>
          i.id === producto.id
            ? { ...i, cantidad: i.cantidad + 1 }
            : i
        );
      }
      return [...prev, { ...producto, cantidad: 1 }];
    });
  };

  const cambiarCantidad = (id, delta) => {
    setCarrito(prev =>
      prev
        .map(i =>
          i.id === id
            ? { ...i, cantidad: i.cantidad + delta }
            : i
        )
        .filter(i => i.cantidad > 0)
    );
  };

  const total = carrito.reduce(
    (sum, i) => sum + i.cantidad * i.precio,
    0
  );

  /* =========================
     COBRAR
  ========================== */

  const cobrar = async () => {
    if (carrito.length === 0) return;

    const ventaId = await state.registrarVenta({
        items: carrito,
        metodoPago
    });

    if (!ventaId) return;

    const venta = {
        id: ventaId,
        fecha: new Date().toISOString(),
        items: carrito.map(i => ({
        nombre: i.nombre,
        cantidad: i.cantidad,
        precioUnitario: i.precio,
        subtotal: i.cantidad * i.precio
        })),
        total,
        metodoPago
    };

    if (state.config.imprimeTicket) {
        generarTicketVenta(venta, state.config);
    }

    setCarrito([]);
    handleSync();
    };


  const categoriasExistentes = [
      ...new Set(state.productos.map(p => p.categoria).filter(Boolean))
  ];

  /* ========================= */

  return (
    <Grid container spacing={3}>
      {syncError && <Grid item xs={12}><Alert severity="error">{syncError}</Alert></Grid>}
      
      {/* PRODUCTOS */}
      <Grid item xs={12} md={7}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          mb={2}
        >
          <Typography variant="h5">Menú</Typography>

          <Select
            size="small"
            value={categoriaFiltro}
            onChange={e => setCategoriaFiltro(e.target.value)}
          >
            {categorias.map(cat => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </Select>

          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder="Buscar producto"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
            <Button
              variant="outlined"
              disabled={
                syncing || (status !== 'ACTIVE' && status !== 'EXPIRING')
              }
              onClick={() => setOpenNuevo(true)}
            >
              + Producto
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          {productosFiltrados.map(p => (
            <Grid item xs={6} sm={4} key={p.id}>
              <Card
                sx={{
                  cursor: "pointer",
                  textAlign: "center",
                  bgcolor: "#fafafa",
                  "&:hover": { bgcolor: "#f0f0f0" }
                }}
                onClick={() => agregarProducto(p)}
              >
                <CardContent>
                  <Typography fontWeight="bold">
                    {p.nombre}
                  </Typography>
                  <Typography variant="body2">
                    {state.config.moneda} {p.precio.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {productosFiltrados.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center">
                No hay productos que coincidan
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* CARRITO */}
      <Grid item xs={12} md={5}>
        <Typography variant="h5" gutterBottom>
          Venta
        </Typography>

        <Card>
          <CardContent>
            {carrito.length === 0 && (
              <Typography color="text.secondary">
                Agrega productos
              </Typography>
            )}

            <Stack spacing={2}>
              {carrito.map(item => (
                <Stack
                  key={item.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography fontWeight="bold">
                      {item.nombre}
                    </Typography>
                    <Typography variant="caption">
                      {item.cantidad} × {item.precio.toFixed(2)}
                    </Typography>
                  </Box>

                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Button onClick={() => cambiarCantidad(item.id, -1)}>
                      -
                    </Button>
                    <Button onClick={() => cambiarCantidad(item.id, 1)}>
                      +
                    </Button>
                    <IconButton
                      color="error"
                      onClick={() =>
                        setCarrito(carrito.filter(i => i.id !== item.id))
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h4" align="center">
              {state.config.moneda} {total.toFixed(2)}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Select
              fullWidth
              value={metodoPago}
              onChange={e => setMetodoPago(e.target.value)}
              sx={{ mb: 2 }}
            >
              <MenuItem value="EFECTIVO">Efectivo</MenuItem>
              <MenuItem value="QR">QR</MenuItem>
              <MenuItem value="TARJETA">Tarjeta</MenuItem>
            </Select>

            <Button
              fullWidth
              size="large"
              variant="contained"
              color="success"
              onClick={cobrar}
              disabled={
                syncing || (status !== 'ACTIVE' && status !== 'EXPIRING')
              }
            >
              COBRAR
            </Button>
          </CardContent>
        </Card>
      </Grid>
      <Dialog open={openNuevo} onClose={() => setOpenNuevo(false)}>
        <DialogTitle>Nuevo Producto</DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
            <Stack spacing={2}>
            <TextField
                label="Nombre"
                value={nuevoProd.nombre}
                onChange={e =>
                setNuevoProd({ ...nuevoProd, nombre: e.target.value })
                }
            />

            <Autocomplete
                freeSolo
                options={categoriasExistentes}
                value={nuevoProd.categoria}
                onInputChange={(_, newValue) =>
                    setNuevoProd({ ...nuevoProd, categoria: newValue })
                }
                renderInput={(params) => (
                    <TextField
                    {...params}
                    label="Categoría"
                    placeholder="POLLO / HAMBURGUESA / BEBIDA"
                    />
                )}
            />


            <TextField
                label="Precio"
                type="number"
                value={nuevoProd.precio}
                onChange={e =>
                setNuevoProd({ ...nuevoProd, precio: e.target.value })
                }
            />
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={() => setOpenNuevo(false)}>Cancelar</Button>
            <Button
            variant="contained"
            onClick={async () => {
                if (!nuevoProd.nombre || !nuevoProd.precio) return;

                await state.agregarProducto({
                nombre: nuevoProd.nombre,
                categoria: nuevoProd.categoria,
                precio: nuevoProd.precio
                });
                handleSync();

                setNuevoProd({ nombre: "", categoria: "", precio: "" });
                setOpenNuevo(false);
            }}
            >
            Guardar
            </Button>
        </DialogActions>
        </Dialog>
        {/* ================= LOADER GLOBAL ================= */}
        <Backdrop
          open={syncing}
          sx={{
            color: '#fff',
            zIndex: theme => theme.zIndex.drawer + 999,
            flexDirection: 'column'
          }}
        >
          <CircularProgress color="inherit" size={60} />
          <Typography mt={2} fontWeight="bold">
            Sincronizando información…
          </Typography>
        </Backdrop>

        {/* ================= FEEDBACK OK ================= */}
        {done && (
          <Backdrop
            open
            sx={{
              color: '#fff',
              zIndex: theme => theme.zIndex.drawer + 999,
              flexDirection: 'column',
              bgcolor: 'rgba(0,0,0,0.6)'
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
            <Typography mt={2} fontWeight="bold">
              Información sincronizada
            </Typography>
          </Backdrop>
        )}

    </Grid>
  );
}
